/**
 * Dominant jersey colour from a cluster's sample crops.
 *
 * Torso band of each crop: middle 50% horizontally, 20-55% vertically (skips
 * head, shorts, and most background). Grass-green pixels are dropped, the
 * rest are quantized to 16 levels per channel, and the modal bin's mean is
 * the colour — mode-then-mean handles striped kits better than a global
 * median, which blends both stripes into mud.
 *
 * Ported unchanged from the Jinja page; the reviewer can still override the
 * result with the colour input beside each crop row.
 */

function loadCropImage(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => resolve(null)
    img.src = url
  })
}

export async function dominantJerseyHex(urls: string[]): Promise<string | null> {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) return null
  const bins = new Map<number, { n: number; r: number; g: number; b: number }>()

  for (const url of urls.slice(0, 12)) {
    const img = await loadCropImage(url)
    if (!img || !img.naturalWidth) continue
    canvas.width = img.naturalWidth
    canvas.height = img.naturalHeight
    ctx.drawImage(img, 0, 0)
    const x0 = Math.floor(canvas.width * 0.25)
    const y0 = Math.floor(canvas.height * 0.20)
    const w = Math.max(1, Math.floor(canvas.width * 0.50))
    const h = Math.max(1, Math.floor(canvas.height * 0.35))
    let data: Uint8ClampedArray
    try {
      data = ctx.getImageData(x0, y0, w, h).data
    } catch {
      return null   // tainted canvas (shouldn't happen: same-origin proxy)
    }
    for (let p = 0; p < data.length; p += 4) {
      // Non-null by construction: p < data.length and the array is RGBA,
      // but the index signature does not know that.
      const r = data[p] as number, g = data[p + 1] as number, b = data[p + 2] as number
      if (g > 60 && g > r * 1.15 && g > b * 1.15) continue   // grass
      const key = ((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4)
      let e = bins.get(key)
      if (!e) { e = { n: 0, r: 0, g: 0, b: 0 }; bins.set(key, e) }
      e.n++; e.r += r; e.g += g; e.b += b
    }
  }

  let best: { n: number; r: number; g: number; b: number } | null = null
  bins.forEach((e) => { if (!best || e.n > best.n) best = e })
  if (!best) return null
  const top = best as { n: number; r: number; g: number; b: number }
  if (!top.n) return null
  const hx = (v: number) => Math.round(v / top.n).toString(16).padStart(2, '0')
  return `#${hx(top.r)}${hx(top.g)}${hx(top.b)}`
}
